// Placed 3D characters: a CharacterDefinition instance positioned in the
// level, optionally walked between authored waypoints by the same
// waypoint-patrol primitive the 2D NPC runtime uses (see
// runtime/twod/npc2d.ts and runtime/waypointPatrol.ts). Split out of
// LevelRenderer.tsx the same way npc2d.ts split out of Tilemap2DScene.tsx.
//
// No blending between poses: a placed character's pose snaps between an
// idle and a walk `CharacterPose`, the same instantaneous pose application
// every character renderer already uses (see rendererRegistry.ts's
// `applyPose`) - this only decides which of the two to show, not how it
// gets there.
//
// A placed character also doubles as a cutscene `CutsceneActor` (see the
// registration effect inside `PlacedCharacter` below): an authored actor
// track's clip beat picks between the same idle/walk pose pair, rather than
// driving a clip-based mixer this renderer model has no concept of at all
// (see `resolveCutsceneClipPose`'s own doc comment for why).
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import type { Group } from 'three';

import type { CharacterDefinition, CharacterPose, CharacterPoseLibrary } from '../content/schema/character';
import type { MapCharacterInstance, MapFloorLevel } from '../content/schema/map';
import { getCharacterRenderer } from '../characters/rendererRegistry';
import { CLIP_GROUPS } from '../characters/animation/clips';
import type { CutsceneActor } from './CutscenePlayer';
import { INITIAL_PATROL_CLOCK, patrolSampleAt, stepPatrolClock, type PatrolClock } from './waypointPatrol';

/** The pose a placed character shows while stationary or dwelling: its
 * definition's own `defaultPoseId` if that id resolves, else the rig's
 * first pose, else no pose at all (the renderer's own rest pose). */
export function resolveIdlePose(definition: CharacterDefinition, poses: CharacterPose[]): CharacterPose | null {
  return poses.find((p) => p.id === definition.defaultPoseId) ?? poses[0] ?? null;
}

/** The pose a placed character shows while a patrol is moving it: the
 * rig's first pose tagged 'walk', falling back to the idle pose when none
 * is authored - the same graceful degrade `spriteAnimationCells` already
 * gives a 2D sprite placed with no 'walk' clip. */
export function resolveWalkPose(poses: CharacterPose[], idlePose: CharacterPose | null): CharacterPose | null {
  return poses.find((p) => p.tags?.includes('walk')) ?? idlePose;
}

// Canonical rig clip names (`ActorKey.clipId` on a cutscene actor track,
// always a real `PrototypeClip` - see `stepCutscene`'s own `isPrototypeClip`
// gate in CutscenePlayer.tsx, which never lets anything else through) that
// read as forward locomotion rather than a stationary beat. Derived from the
// shared rig's own locomotion group, minus its two stationary members, so a
// clip added there later is picked up automatically instead of needing a
// hand-maintained duplicate list here.
const CUTSCENE_WALK_CLIPS = new Set<string>(
  CLIP_GROUPS.locomotion.filter((name) => !name.includes('Idle')),
);

/**
 * The pose a placed character should show for an authored cutscene
 * actor-clip beat. A placed character has no clip-driven animation mixer of
 * its own (see this file's banner comment: a renderer only ever receives a
 * `CharacterPose`, snapped to instantly) - so "playing a clip" here means
 * picking whichever of its own idle/walk poses the clip's name reads
 * closest to, not literally animating that clip. This is the honest limit
 * of what the existing pose model can show, not a stand-in for real
 * playback: a character with richer authored poses still only ever shows
 * one of these two during a cutscene, exactly as it does outside one.
 */
export function resolveCutsceneClipPose(
  clip: string,
  idlePose: CharacterPose | null,
  walkPose: CharacterPose | null,
): CharacterPose | null {
  return CUTSCENE_WALK_CLIPS.has(clip) ? walkPose : idlePose;
}

/** Where a placed character starts, before its first `useFrame` tick: its
 * patrol's own sample at elapsed 0 when it has one (so the mesh doesn't
 * flash at `instance.position` for a frame before jumping to the route),
 * its authored `position` otherwise. */
export function initialCharacterPosition(instance: MapCharacterInstance, floorOffsetY: number): [number, number, number] {
  if (!instance.patrol) {
    const [x, y, z] = instance.position;
    return [x, y + floorOffsetY, z];
  }
  const sample = patrolSampleAt(instance.patrol, 0);
  return [sample.position.x, sample.position.y + floorOffsetY, sample.position.z];
}

function PlacedCharacter({
  instance,
  definition,
  poses,
  floorOffsetY,
  cutsceneActorsRef,
}: {
  instance: MapCharacterInstance;
  definition: CharacterDefinition;
  poses: CharacterPose[];
  floorOffsetY: number;
  /** See `PlacedCharactersProps`' own doc comment. */
  cutsceneActorsRef?: MutableRefObject<Record<string, CutsceneActor>>;
}) {
  const Renderer = getCharacterRenderer(definition.rendererId);
  const groupRef = useRef<Group>(null);
  const patrolClockRef = useRef<PatrolClock>(INITIAL_PATROL_CLOCK);

  const idlePose = useMemo(() => resolveIdlePose(definition, poses), [definition, poses]);
  const walkPose = useMemo(() => resolveWalkPose(poses, idlePose), [poses, idlePose]);
  const [activePose, setActivePose] = useState<CharacterPose | null>(idlePose);

  // Latest-value refs so the CutsceneActor registered below (created once,
  // in the effect after this) always drives from this render's own
  // idle/walk poses without needing to be re-created every time they change
  // identity (poseLibraries is recomputed - and so is `poses` - on every
  // PlacedCharacters re-render, not just when the authored poses actually
  // change).
  const idlePoseRef = useRef(idlePose); idlePoseRef.current = idlePose;
  const walkPoseRef = useRef(walkPose); walkPoseRef.current = walkPose;

  // True only for the span of frames a cutscene actor track is actively
  // driving this placement's pose (see the registration effect below) -
  // read-and-reset every patrol tick, re-armed every frame `play()` is
  // actually called (CutsceneActor's own contract: called every frame a
  // clip is active). This self-clears within one frame of the cutscene
  // stopping (ending, or never having started for this actorId) with no
  // separate "release" signal needed, and is correct regardless of whether
  // this component's own useFrame or CutscenePlayer's runs first in a given
  // frame - whichever order, the flag converges to the right value every
  // frame after the first.
  const cutsceneDrivenRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- first-paint seed only; kept in sync after mount by the effect below (static) or useFrame (patrol).
  const initialPosition = useMemo(() => initialCharacterPosition(instance, floorOffsetY), []);

  // A patrol drives its own position every frame via useFrame below; a
  // static placement has no such per-frame driver, so without this it would
  // stay frozen at `initialPosition` forever - even a live edit to its
  // floor/X/Y/Z in Studio's Placements panel (which re-renders this
  // component with a new `instance`) would never actually move the mesh.
  useEffect(() => {
    const group = groupRef.current;
    if (!group || instance.patrol) return;
    const [x, y, z] = instance.position;
    group.position.set(x, y + floorOffsetY, z);
  }, [instance.patrol, instance.position, floorOffsetY]);

  // Registers this placement as a `CutsceneActor` so an authored actor
  // track can actually drive it (see `resolveCutsceneClipPose`'s own doc
  // comment for what "driving" means for a pose-snap renderer). Keyed by
  // both the roster `characterId` - what CutsceneTimeline's own actor-id
  // suggestions offer an author, see `ActorFields` in CutsceneTimeline.tsx -
  // and this specific placement's own `id`, so an authored track resolves
  // regardless of which one it was written against; two placements sharing
  // one characterId simply have the later-mounted one win that shared key,
  // each still individually reachable by its own instance id. A no-op when
  // the host doesn't wire a registry at all (e.g. the map-builder's own
  // LevelRenderer mount, which never runs a cutscene against this scene).
  useEffect(() => {
    if (!cutsceneActorsRef) return;
    const registry = cutsceneActorsRef.current;
    const cutsceneActor: CutsceneActor = {
      play(clip) {
        cutsceneDrivenRef.current = true;
        const pose = resolveCutsceneClipPose(clip, idlePoseRef.current, walkPoseRef.current);
        setActivePose((current) => (current === pose ? current : pose));
      },
    };
    registry[instance.characterId] = cutsceneActor;
    registry[instance.id] = cutsceneActor;
    return () => {
      if (registry[instance.characterId] === cutsceneActor) delete registry[instance.characterId];
      if (registry[instance.id] === cutsceneActor) delete registry[instance.id];
    };
  }, [cutsceneActorsRef, instance.characterId, instance.id]);

  useFrame((_, dt) => {
    const group = groupRef.current;
    if (!group || !instance.patrol) return;
    // A cutscene actor track owns this placement's pose for as long as it
    // keeps calling play() - the patrol driver below would otherwise fight
    // it, overwriting the authored pose with idle/walk every single frame.
    // `ActorKey` carries no position data (just t/actorId/clipId - see its
    // own schema), so actually moving a placed character during a cutscene
    // is not something this data model can express; holding it exactly
    // where the patrol last left it while it poses is the honest
    // approximation, not silently faking travel the schema cannot author.
    if (cutsceneDrivenRef.current) {
      cutsceneDrivenRef.current = false; // re-armed below (play()) if still active this frame
      return;
    }
    patrolClockRef.current = stepPatrolClock(patrolClockRef.current, dt);
    const sample = patrolSampleAt(instance.patrol, patrolClockRef.current.elapsedMs);
    group.position.set(sample.position.x, sample.position.y + floorOffsetY, sample.position.z);
    if (!sample.dwelling && (sample.direction.x !== 0 || sample.direction.z !== 0)) {
      group.rotation.y = Math.atan2(sample.direction.x, sample.direction.z);
    }
    const nextPose = sample.dwelling ? idlePose : walkPose;
    if (nextPose !== activePose) setActivePose(nextPose);
  });

  if (!Renderer) return null;

  return (
    <group ref={groupRef} position={initialPosition} rotation={[0, instance.rotationY ?? 0, 0]}>
      {/* eslint-disable-next-line react-hooks/static-components -- resolved from the character registry, a stable module-level component. */}
      <Renderer pose={activePose} />
    </group>
  );
}

export interface PlacedCharactersProps {
  instances: MapCharacterInstance[];
  characters: CharacterDefinition[];
  poseLibraries: CharacterPoseLibrary[];
  floorOffsetY: (floor: MapFloorLevel) => number;
  /** A shared, mutable `CutsceneActor` registry every placed character
   * writes itself into (and back out of on unmount) - `GameRuntime` owns
   * this ref and hands the very same object to `CutscenePlayer`'s own
   * `actors` prop, so an authored actor track resolves to a real placed
   * character. Omitted by a host that never runs a cutscene against this
   * scene (e.g. the map builder's own `LevelRenderer` mount), in which case
   * registration is simply skipped. */
  cutsceneActorsRef?: MutableRefObject<Record<string, CutsceneActor>>;
}

/** Every placed character whose `characterId` resolves to a real
 * `CharacterDefinition` and whose `rendererId` resolves to a registered
 * renderer - either missing skips the instance rather than crashing the
 * scene, the same convention `Tilemap2DEntity`'s dangling `spriteId`
 * already follows (see `content/schema/map.ts`'s `MapCharacterInstance`
 * doc comment). */
export function PlacedCharacters({ instances, characters, poseLibraries, floorOffsetY, cutsceneActorsRef }: PlacedCharactersProps) {
  const byId = useMemo(() => new Map(characters.map((c) => [c.id, c])), [characters]);
  if (instances.length === 0) return null;
  return (
    <>
      {instances.map((instance) => {
        const definition = byId.get(instance.characterId);
        if (!definition) return null;
        const poses = poseLibraries.filter((lib) => lib.rigId === definition.rigId).flatMap((lib) => lib.poses);
        return (
          <PlacedCharacter
            key={instance.id}
            instance={instance}
            definition={definition}
            poses={poses}
            floorOffsetY={floorOffsetY(instance.floor)}
            cutsceneActorsRef={cutsceneActorsRef}
          />
        );
      })}
    </>
  );
}
